<script>
	import { onMount } from "svelte";
	import { categoryGen2All as all } from "../../stores";
	import { postApi as post, putApi as put, delApi as del } from "../../service/api";
	import { Dates } from "../../utils/date";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import Tab from "../../components/Tab.svelte";

	// 상수 정의
	const TYPES = {
		sleep: { value: 1, text: "Sleep" },
		meditation: { value: 2, text: "Meditation" },
		focus: { value: 3, text: "Focus" },
	};

	const ACTIVE_OPTIONS = [
		{ value: 1, text: "사용" },
		{ value: 0, text: "미사용" },
	];

	const LANGUAGES = [
		{ field: "subjectKo", label: "한국어" },
		{ field: "subjectEn", label: "영어" },
		{ field: "subjectJa", label: "일본어" },
		{ field: "subjectZhCn", label: "중국어간체" },
		{ field: "subjectZhTw", label: "중국어번체" },
	];

	// 상태 관리
	let isEditMode = false;
	let editingRowId = null;
	let focusField = "subjectKo";
	let activeTab = "all";
	let filteredData = [];
	let currentPage = 1;
	let totalPages = 1;
	let totalItems = 0;
	let pageSize = 10;
	const PAGE_SIZES = [10, 20, 50, 100];

	// 편집 아이템 초기값
	const defaultEditItem = {
		id: 0,
		type: 1,
		subjectKo: "",
		subjectEn: "",
		subjectJa: "",
		subjectZhCn: "",
		subjectZhTw: "",
		customOrder: 0,
		isLoop: 0,
		isActive: 0,
	};

	let editItem = { ...defaultEditItem };

	// 메서드
	const setFocus = (field = "subjectKo") => {
		focusField = field;
	};

	const validateItem = () => {
		for (const { field, label } of LANGUAGES) {
			if (!editItem[field]) {
				alert(`${label}을(를) 입력하세요.`);
				setFocus(field);
				return false;
			}
		}
		return true;
	};

	const handleSave = async () => {
		if (!validateItem()) return;

		const payload = {
			...editItem,
			type: parseInt(editItem.type),
			isActive: editItem.id ? parseInt(editItem.isActive) === 1 : undefined,
		};

		(await editItem.id) ? put("/contents/category/gen2", payload) : post("/contents/category/gen2", payload);

		setTimeout(async () => {
			await fnSearch();
		}, 100);
	};

	const fnSearch = async () => {
		await all.fetch("/contents/category/gen2");
		resetEdit();
	};

	const resetEdit = () => {
		isEditMode = false;
		editingRowId = null;
		editItem = { ...defaultEditItem };
	};

	const fnProcessRegist = () => {
		if (!isEditMode) {
			isEditMode = true;
			editingRowId = null;
			editItem = { ...defaultEditItem };
			focusFirstInput();
		}
	};

	const focusFirstInput = () => {
		setTimeout(() => {
			const firstInput = document.querySelector('input[type="text"]');
			if (firstInput) {
				firstInput.focus();
				firstInput.select();
			}
		});
	};

	const fnProcessEdit = (item) => {
		if (!isEditMode) {
			isEditMode = true;
			editingRowId = item.id;
			editItem = {
				...item,
				isActive: item.isActive ? 1 : 0,
			};
			focusFirstInput();
		}
	};

	const fnDelete = async (id) => {
		const item = $all.data.find((item) => item.id === id);
		if (item.isActive) {
			alert("사용 중인 카테고리는 삭제할 수 없습니다.");
			return;
		}

		if (confirm("정말 삭제하시겠습니까?")) {
			await del("/contents/category/gen2", { id });
			await fnSearch();
		}
	};

	const fnRegistServer = () => {
		if (confirm("서버에 적용하시겠습니까?")) {
			put("/contents/category/gen2/cache", {}).then((success) => {
				if (success) {
					alert("서버 적용이 완료되었습니다.");
				}
			});
		}
	};

	// 필터링된 데이터를 반환하는 함수
	const getFilteredData = (data) => {
		if (!data) return [];
		if (activeTab === "all") return data;

		const typeValue = TYPES[activeTab]?.value;
		return data.filter((item) => item.type === typeValue);
	};

	// 탭 변경 핸들러
	const handleTabChange = (tab) => {
		activeTab = tab;
		fnSearch();
	};

	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value); // pageSize 값 업데이트
		currentPage = 1; // 페이지를 1로 리셋
		fnSearch(); // 목록 새로고침
	};

	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fnSearch();
	};

	$: {
		// activeTab이나 $all.data가 변경될 때마다 실행
		console.log("activeTab changed:", activeTab);
		filteredData = getFilteredData($all.data || []);
	}

	const tabs = [{ key: "all", text: "전체 카테고리" }, ...Object.entries(TYPES).map(([key, type]) => ({ key, text: type.text }))];

	onMount(() => {
		menu.set("컨텐츠 관리");
		menuSub.set("장르 관리");
		menuSubSub.set("컨텐츠 장르 (2세대)");
		fnSearch();
	});
</script>

<div class="card mb-4">
	<div class="table-responsive text-nowrap">
		<Tab {tabs} {activeTab} onTabChange={handleTabChange} />
		<div class="ms-auto" style="padding: 5px;">
			<TableToolbar
				{totalItems}
				{currentPage}
				{totalPages}
				{pageSize}
				pageSizes={PAGE_SIZES}
				onPageSizeChange={handlePageSizeChange}
				onReload={fnSearch}
				onRegist={fnProcessRegist}
				onRegistServer={fnRegistServer}
			/>
		</div>
		<div class="fade-container {activeTab ? 'show' : ''}">
			<!-- 메인 테이블 -->
			<table class="table small">
				<thead>
					<tr style="text-align:center">
						<th width="80">No</th>
						<th width="150">분류</th>
						<th width="150">정렬 순서</th>
						<th width="150">루프 여부</th>
						{#each LANGUAGES as { label }}
							<th width={label === "한국어" ? "*" : "210"}>
								<span on:click={() => setFocus(label)} style="cursor: pointer;">{label}</span>
							</th>
						{/each}
						<th width="150">사용여부</th>
						<th width="150">등록일시</th>
						<th width="150">수정일시</th>
						<th width="150">관리</th>
					</tr>
				</thead>
				<tbody>
					{#if isEditMode && !editingRowId}
						<tr style="text-align:center">
							<td />
							<td>
								<select class="form-select form-select-sm" bind:value={editItem.type}>
									{#each Object.values(TYPES) as { value, text }}
										<option {value}>{text}</option>
									{/each}
								</select>
							</td>
							<td>
								<input
									type="number"
									class="form-control form-control-sm"
									bind:value={editItem.customOrder}
									on:focus={() => setFocus("customOrder")}
								/>
							</td>
							<td>
								<div class="form-switch">
									<input class="form-check-input" type="checkbox" id="isLoop" bind:checked={editItem.isLoop} />
								</div>
							</td>
							{#each LANGUAGES as { field }}
								<td>
									<input
										type="text"
										class="form-control form-control-sm"
										style="text-align: center"
										bind:value={editItem[field]}
										on:focus={() => setFocus(field)}
									/>
								</td>
							{/each}
							<td>
								<select class="form-select form-select-sm" bind:value={editItem.isActive}>
									{#each ACTIVE_OPTIONS as option}
										<option value={option.value}>{option.text}</option>
									{/each}
								</select>
							</td>
							<td />
							<td />
							<td>
								<button class="btn btn-sm btn-primary me-1" on:click={handleSave}>Save</button>
								<button class="btn btn-sm btn-outline-secondary" on:click={resetEdit}>Cancel</button>
							</td>
						</tr>
					{/if}

					{#each $all.data ? filteredData : [] as item, index}
						{#if editingRowId === item.id}
							<tr style="text-align:center">
								<td>{index + 1}</td>
								<td>
									<select class="form-select form-select-sm" bind:value={editItem.type}>
										{#each Object.values(TYPES) as { value, text }}
											<option {value}>{text}</option>
										{/each}
									</select>
								</td>
								<td>
									<input
										type="number"
										class="form-control form-control-sm"
										bind:value={editItem.customOrder}
										on:focus={() => setFocus("customOrder")}
									/>
								</td>
								<td>
									<div class="form-switch">
										<input class="form-check-input" type="checkbox" id="isLoop" bind:checked={editItem.isLoop}/>
									</div>
								</td>
								{#each LANGUAGES as { field }}
									<td>
										<input
											type="text"
											class="form-control form-control-sm"
											style="text-align: center"
											bind:value={editItem[field]}
											on:focus={() => setFocus(field)}
										/>
									</td>
								{/each}
								<td>
									<select class="form-select form-select-sm" bind:value={editItem.isActive}>
										{#each ACTIVE_OPTIONS as option}
											<option value={option.value}>{option.text}</option>
										{/each}
									</select>
								</td>
								<td>{Dates.defaultConvertFull(item.createdAt)}</td>
								<td>{Dates.defaultConvertFull(item.updatedAt)}</td>
								<td>
									<button class="btn btn-sm btn-primary me-1" on:click={handleSave}>Save</button>
									<button class="btn btn-sm btn-outline-secondary" on:click={resetEdit}>Cancel</button>
								</td>
							</tr>
						{:else}
							<tr style="text-align:center">
								<td>{index + 1}</td>
								<td><b>{TYPES[Object.keys(TYPES).find((key) => TYPES[key].value === item.type)]?.text}</b></td>
								<td>{item.customOrder}</td>
								<td>
									<div class="form-switch">
										<input class="form-check-input" type="checkbox" id="isLoop" bind:checked={item.isLoop} disabled/>
									</div>
								</td>
								{#each LANGUAGES as { field }}
									<td>{item[field]}</td>
								{/each}
								<td>
									{#if item.isActive}<b>사용</b>{:else}미사용{/if}
								</td>
								<td>{Dates.defaultConvertFull(item.createdAt)}</td>
								<td>{Dates.defaultConvertFull(item.updatedAt)}</td>
								<td>
									<button class="btn btn-sm btn-outline-primary me-1" on:click={() => fnProcessEdit(item)}>Edit</button>
									<button class="btn btn-sm btn-outline-primary" on:click={() => fnDelete(item.id)}>Delete</button>
								</td>
							</tr>
						{/if}
					{/each}
				</tbody>
			</table>
		</div>
		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
</div>

<style>
	.fade-container {
		opacity: 0;
		transition: opacity 0.15s linear;
	}

	.fade-container.show {
		opacity: 1;
	}
</style>
